'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Loader2, Ticket, Undo2 } from 'lucide-react';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { CommunityRaffle } from '@/types/raffle';
import { countdownParts, countdownTarget, formatCountdown, formatDate, progressPercent, winChance } from '@/app/raffles/utils';
import { Label, PrizeAmount, PrizeTypeTag, ProgressBar, StatusTag, WinnerRow } from '@/app/raffles/components/RaffleBits';
import EntryForm from '@/app/raffles/components/EntryForm';
import StakeLogo from '@/app/components/Navbar/StakeLogo';
import EntriesList from '@/app/raffles/components/EntriesList';

interface RaffleRowProps {
    raffle: CommunityRaffle;
    now: number;
    open: boolean;
    onToggle: () => void;
    onUpdated: (raffle: CommunityRaffle) => void;
}

/** Header-cell text for the timing column, by phase. */
const timingLabel = (raffle: CommunityRaffle) => ({
    upcoming: 'Opens in',
    active: 'Draw in',
    ended: 'Draw',
    finished: 'Drawn',
    cancelled: 'Draw time',
}[raffle.status]);

function Cell({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="min-w-0">
            <Label>{label}</Label>
            <div className="mt-0.5 truncate text-sm font-mono font-bold text-white tabular-nums">{children}</div>
        </div>
    );
}

/**
 * One raffle as a compact row; click to expand the entry form and Entries list.
 * Every raffle — live or past — uses the same row, so the page reads as one list.
 * Surfaces follow the partner cards on /bonuses.
 */
export default function RaffleRow({ raffle, now, open, onToggle, onUpdated }: RaffleRowProps) {
    const { user } = useAuth();
    const myTickets = raffle.my_tickets ?? 0;
    const isPast = raffle.status === 'finished' || raffle.status === 'cancelled';
    const showCountdown = raffle.status === 'upcoming' || raffle.status === 'active';
    const chance = winChance(raffle);

    const timing = showCountdown
        ? formatCountdown(countdownTarget(raffle) - now)
        : raffle.status === 'ended'
            ? <span className="inline-flex items-center gap-1.5 text-amber-300"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drawing</span>
            : formatDate(raffle.drawn_at ?? raffle.ends_at);

    return (
        <div className="group relative overflow-hidden rounded-2xl backdrop-blur transition-all duration-300">
            {/* Hover sweep, same as the partner cards — off while expanded so the open panel stays neutral */}
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-green-400/5 to-transparent transition-opacity duration-500 ${open ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}`} />

            {/* Live raffles get a thin time-left bar along the top edge */}
            {raffle.status === 'active' && (
                <div className="absolute inset-x-0 top-0">
                    <ProgressBar percent={progressPercent(raffle, now)} />
                </div>
            )}

            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="relative w-full text-left p-4 sm:p-5 grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1.8fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.6fr)_auto] items-center gap-x-4 gap-y-3"
            >
                {/* Title block */}
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <StatusTag raffle={raffle} />
                        <PrizeTypeTag raffle={raffle} />
                        {raffle.stake_only && (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-sky-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-sky-300">
                                <StakeLogo size={11} /> Stake only
                            </span>
                        )}
                        {myTickets > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-lg bg-green-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-green-400">
                                <Ticket className="w-3 h-3" /> Entered with {myTickets.toLocaleString()}
                            </span>
                        )}
                    </div>
                    <h3 className="mt-2 truncate text-base sm:text-lg font-mono font-bold text-white">{raffle.title}</h3>
                </div>

                {/* Chevron sits top-right on mobile, last column on desktop */}
                <ChevronDown className={`md:order-last w-5 h-5 shrink-0 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180 text-green-400' : 'group-hover:text-white'}`} />

                {/* Stats — a 2×2 grid on mobile, flattened into the row's columns on desktop */}
                <div className="col-span-2 grid grid-cols-2 gap-3 md:contents">
                    <div className="min-w-0">
                        <Label>Prize</Label>
                        <div className={`mt-0.5 truncate text-2xl font-mono font-bold ${isPast ? 'text-gray-300' : 'bg-gradient-to-r from-green-300 via-emerald-400 to-green-500 bg-clip-text text-transparent'}`}>
                            <PrizeAmount raffle={raffle} size={20} />
                        </div>
                    </div>
                    <Cell label={timingLabel(raffle)}>{timing}</Cell>
                    <Cell label="Entered tickets">{raffle.total_tickets.toLocaleString()}</Cell>
                    <Cell label="Entries">{raffle.participants.toLocaleString()}</Cell>
                </div>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeOut' }}
                        className="relative overflow-hidden"
                    >
                        <div className="mx-4 sm:mx-5 mb-5 grid gap-6 pt-1 md:grid-cols-2">
                            {/* Left — timing, your entry, form */}
                            <div className="flex flex-col gap-4 min-w-0">
                                {showCountdown && (
                                    <div>
                                        <Label>{raffle.status === 'upcoming' ? 'Opens in' : 'Draw in'}</Label>
                                        <div className="mt-2 grid grid-cols-4 gap-2">
                                            {countdownParts(countdownTarget(raffle) - now).map(p => (
                                                <div key={p.label} className="rounded-xl bg-gradient-to-br from-emerald-600/20 via-emerald-800/20 to-gray-900/20 py-2.5 text-center">
                                                    <div className="text-2xl font-mono font-bold text-white tabular-nums leading-none">{p.value}</div>
                                                    <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-gray-400">{p.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {raffle.status === 'ended' && (
                                    <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 px-3 py-3 text-sm font-mono text-amber-300">
                                        <Loader2 className="w-4 h-4 animate-spin" /> Picking a winner — this takes up to a minute.
                                    </div>
                                )}

                                {raffle.status === 'finished' && (
                                    <div>
                                        <Label>Winner</Label>
                                        <div className="mt-2"><WinnerRow raffle={raffle} viewerId={user?.id} /></div>
                                    </div>
                                )}

                                {raffle.status === 'cancelled' && (
                                    <p className="flex items-center gap-2 rounded-xl bg-gray-800/40 px-3 py-3 text-sm font-mono text-gray-400">
                                        <Undo2 className="w-4 h-4 shrink-0" /> Raffle cancelled — every ticket was refunded.
                                    </p>
                                )}

                                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl bg-gray-800/40 p-3 text-xs font-mono">
                                    <dt className="text-gray-400">Min – max tickets</dt>
                                    <dd className="text-right font-bold text-white tabular-nums">
                                        {raffle.min_tickets.toLocaleString()} – {raffle.max_tickets.toLocaleString()}
                                    </dd>
                                    <dt className="text-gray-400">{raffle.status === 'upcoming' ? 'Opens' : 'Opened'}</dt>
                                    <dd className="text-right text-gray-200 tabular-nums">{formatDate(raffle.starts_at)}</dd>
                                    <dt className="text-gray-400">Draw time</dt>
                                    <dd className="text-right text-gray-200 tabular-nums">{formatDate(raffle.ends_at)}</dd>
                                    {myTickets > 0 && (
                                        <>
                                            <dt className="text-gray-400">Entered with</dt>
                                            <dd className="text-right font-bold text-green-400 tabular-nums">
                                                {myTickets.toLocaleString()} {myTickets === 1 ? 'ticket' : 'tickets'}
                                            </dd>
                                        </>
                                    )}
                                    {myTickets > 0 && !isPast && chance && (
                                        <>
                                            <dt className="text-gray-400">Win chance</dt>
                                            <dd className="text-right font-bold text-white tabular-nums">{chance}</dd>
                                        </>
                                    )}
                                </dl>

                                <EntryForm raffle={raffle} onUpdated={onUpdated} />
                            </div>

                            {/* Right — who's in */}
                            <EntriesList raffle={raffle} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
